import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  styled,
  IconButton,
  Divider,
  Alert,
} from '@mui/material'
import { Edit, Check, Close, CameraAlt, Delete, CloudUpload } from '@mui/icons-material'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import {
  useUpdateProfile,
  useUploadProfilePicture,
  useUploadCoverPicture,
} from '@/hooks/api/dashboard'
import { LoadingButton } from '@mui/lab'

// ==================== STYLED COMPONENTS ====================
const SectionHeader = styled(Box)({
  marginBottom: '32px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
})

const SectionDescription = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  lineHeight: 1.6,
})

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '16px',
  overflow: 'hidden',
})

const CardHeader = styled(Box)({
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
})

const CardContent = styled(Box)({
  padding: '24px',
})

const FieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '24px',
  marginBottom: '24px',

  '&:last-child': {
    marginBottom: 0,
  },
})

const FieldLabel = styled(Box)({
  width: '200px',
  flexShrink: 0,
  paddingTop: '10px',
})

const FieldContent = styled(Box)({
  flex: 1,
  minWidth: 0,
})

const EditableField = styled(Box)<{ isEditing?: boolean }>(({ isEditing }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: isEditing ? '0' : '10px 12px',
  borderRadius: '8px',
  backgroundColor: isEditing ? 'transparent' : '#f9fafb',
  border: isEditing ? 'none' : '1px solid #e5e7eb',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: isEditing ? 'transparent' : '#f3f4f6',
  },
}))

const AvatarSection = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '24px',
  marginBottom: '32px',
})

const AvatarWrapper = styled(Box)({
  position: 'relative',

  '&:hover .avatar-overlay': {
    opacity: 1,
  },
})

const AvatarOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  cursor: 'pointer',
})

const CoverPhotoContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '200px',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#f3f4f6',
  backgroundSize: 'cover',
  backgroundPosition: 'center',

  '&:hover .cover-overlay': {
    opacity: 1,
  },
})

const CoverOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  opacity: 0,
  transition: 'opacity 0.2s ease',
})

// ==================== COMPONENT ====================
const ProfileSection: React.FC = () => {
  const [userProfile] = useAppStore((state) => [state.user.profile], shallow)

  // Edit states
  const [editingName, setEditingName] = useState(false)
  const [editingUsername, setEditingUsername] = useState(false)
  const [editingMotto, setEditingMotto] = useState(false)

  // Form values
  const [name, setName] = useState(userProfile?.userName || '')
  const [username, setUsername] = useState(userProfile?.userName || '')
  const [motto, setMotto] = useState(userProfile?.motto || '')

  // API hooks
  const updateProfile = useUpdateProfile()
  const uploadProfilePic = useUploadProfilePicture()
  const uploadCoverPic = useUploadCoverPicture()

  const handleSaveName = async () => {
    await updateProfile.mutateAsync({ firstName: name })
    setEditingName(false)
  }

  const handleSaveMotto = async () => {
    await updateProfile.mutateAsync({ motto })
    setEditingMotto(false)
  }

  const handleProfilePicUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      uploadProfilePic.mutate({ file })
    }
  }

  const handleCoverPicUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      uploadCoverPic.mutate({ file })
    }
  }

  return (
    <>
      <SectionHeader>
        <SectionTitle>Profile</SectionTitle>
        <SectionDescription>
          Manage your public profile information and appearance
        </SectionDescription>
      </SectionHeader>

      {/* Cover Photo */}
      <Card>
        <CardContent>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827',
              mb: 2,
            }}
          >
            Cover Photo
          </Typography>
          <CoverPhotoContainer
            sx={{
              backgroundImage: userProfile?.profileCoverURL
                ? `url(${userProfile.profileCoverURL})`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <CoverOverlay className='cover-overlay'>
              <input
                accept='image/*'
                style={{ display: 'none' }}
                id='cover-upload'
                type='file'
                onChange={handleCoverPicUpload}
              />
              <label htmlFor='cover-upload'>
                <Button
                  component='span'
                  variant='contained'
                  startIcon={<CloudUpload />}
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                    },
                  }}
                >
                  Upload
                </Button>
              </label>
              {userProfile?.profileCoverURL && (
                <Button
                  variant='contained'
                  startIcon={<Delete />}
                  sx={{
                    backgroundColor: '#ef4444',
                    '&:hover': {
                      backgroundColor: '#dc2626',
                    },
                  }}
                >
                  Remove
                </Button>
              )}
            </CoverOverlay>
          </CoverPhotoContainer>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#9ca3af',
              mt: 1.5,
            }}
          >
            Recommended size: 1500x500px. Max file size: 5MB.
          </Typography>
        </CardContent>
      </Card>

      {/* Avatar & Basic Info */}
      <Card>
        <CardHeader>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Basic Information
          </Typography>
        </CardHeader>
        <CardContent>
          {/* Avatar */}
          <AvatarSection>
            <AvatarWrapper>
              <Avatar
                src={userProfile?.profilePicThumbURL}
                alt={userProfile?.userName}
                sx={{
                  width: 96,
                  height: 96,
                  fontSize: '32px',
                  fontWeight: 600,
                  border: '3px solid #ffffff',
                  boxShadow: '0 0 0 1px #e5e7eb',
                }}
              >
                {userProfile?.userName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <AvatarOverlay className='avatar-overlay'>
                <input
                  accept='image/*'
                  style={{ display: 'none' }}
                  id='avatar-upload'
                  type='file'
                  onChange={handleProfilePicUpload}
                />
                <label htmlFor='avatar-upload'>
                  <IconButton
                    component='span'
                    sx={{
                      color: '#ffffff',
                    }}
                  >
                    <CameraAlt />
                  </IconButton>
                </label>
              </AvatarOverlay>
            </AvatarWrapper>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 1,
                }}
              >
                Profile Picture
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#6b7280',
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                Upload a profile picture to personalize your account. Square images work best.
              </Typography>
              <input
                accept='image/*'
                style={{ display: 'none' }}
                id='avatar-upload-button'
                type='file'
                onChange={handleProfilePicUpload}
              />
              <label htmlFor='avatar-upload-button'>
                <LoadingButton
                  component='span'
                  variant='outlined'
                  size='small'
                  startIcon={<CloudUpload />}
                  loading={uploadProfilePic.isLoading}
                  sx={{
                    borderColor: '#d1d5db',
                    color: '#4b5563',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                    },
                  }}
                >
                  Change Photo
                </LoadingButton>
              </label>
            </Box>
          </AvatarSection>

          <Divider sx={{ my: 3 }} />

          {/* Display Name */}
          <FieldRow>
            <FieldLabel>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Display Name
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#9ca3af',
                  mt: 0.5,
                }}
              >
                Your public name
              </Typography>
            </FieldLabel>
            <FieldContent>
              {editingName ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size='small'
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                      },
                    }}
                  />
                  <LoadingButton
                    variant='contained'
                    size='small'
                    onClick={handleSaveName}
                    loading={updateProfile.isLoading}
                    sx={{
                      minWidth: '80px',
                      fontSize: '13px',
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: '#3b82f6',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                    }}
                  >
                    <Check sx={{ fontSize: 16, mr: 0.5 }} />
                    Save
                  </LoadingButton>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setName(userProfile?.userName || '')
                      setEditingName(false)
                    }}
                  >
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <EditableField>
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    {userProfile?.userName || 'Not set'}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setEditingName(true)}
                    sx={{
                      color: '#6b7280',
                      '&:hover': {
                        color: '#3b82f6',
                      },
                    }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </EditableField>
              )}
            </FieldContent>
          </FieldRow>

          {/* Bio / Motto */}
          <FieldRow>
            <FieldLabel>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Bio
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#9ca3af',
                  mt: 0.5,
                }}
              >
                Tell us about yourself
              </Typography>
            </FieldLabel>
            <FieldContent>
              {editingMotto ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={motto}
                    onChange={(e) => setMotto(e.target.value)}
                    placeholder='Write a short bio...'
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                      },
                    }}
                  />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: motto.length > 160 ? '#ef4444' : '#9ca3af',
                      }}
                    >
                      {motto.length}/160
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => {
                          setMotto(userProfile?.motto || '')
                          setEditingMotto(false)
                        }}
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        variant='contained'
                        size='small'
                        onClick={handleSaveMotto}
                        loading={updateProfile.isLoading}
                        disabled={motto.length > 160}
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'none',
                          backgroundColor: '#3b82f6',
                          '&:hover': {
                            backgroundColor: '#2563eb',
                          },
                        }}
                      >
                        Save Bio
                      </LoadingButton>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <EditableField>
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: userProfile?.motto ? '#111827' : '#9ca3af',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {userProfile?.motto || 'No bio yet. Click to add one.'}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setEditingMotto(true)}
                    sx={{
                      color: '#6b7280',
                      '&:hover': {
                        color: '#3b82f6',
                      },
                    }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </EditableField>
              )}
            </FieldContent>
          </FieldRow>

          {/* Email (Read-only) */}
          <FieldRow>
            <FieldLabel>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Email
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#9ca3af',
                  mt: 0.5,
                }}
              >
                Your account email
              </Typography>
            </FieldLabel>
            <FieldContent>
              <EditableField>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#111827',
                  }}
                >
                  {userProfile?.userEmail || 'not-set@example.com'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#9ca3af',
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                  }}
                >
                  VERIFIED
                </Typography>
              </EditableField>
            </FieldContent>
          </FieldRow>
        </CardContent>
      </Card>

      {/* Success Message */}
      {(updateProfile.isSuccess || uploadProfilePic.isSuccess || uploadCoverPic.isSuccess) && (
        <Alert
          severity='success'
          sx={{
            mt: 2,
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          Profile updated successfully!
        </Alert>
      )}
    </>
  )
}

export default ProfileSection
